import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Plus, Search, Filter, MoreVertical, Video,
  Building2, Users, Eye, Edit, Trash2, PlayCircle,
  FileText, Mic, CheckCircle, Clock, ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingsList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    meeting_type: searchParams.get('type') || 'all',
    search: ''
  });

  const fetchMeetings = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ token, page, limit: 20 });
      
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.meeting_type !== 'all') params.append('meeting_type', filters.meeting_type);
      
      const res = await axios.get(`${API}/api/meeting-intelligence/meetings?${params}`);
      setMeetings(res.data.meetings || []);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Error fetching meetings:', err);
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'teams': return <Video className="h-5 w-5 text-purple-600" />;
      case 'hybrid': return <Users className="h-5 w-5 text-amber-600" />;
      default: return <Building2 className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: { variant: 'outline', color: 'text-blue-600' },
      in_progress: { variant: 'secondary', color: 'text-amber-600' },
      completed: { variant: 'default', color: 'text-emerald-600' },
      cancelled: { variant: 'destructive', color: 'text-red-600' }
    };
    const config = variants[status] || variants.scheduled;
    return <Badge variant={config.variant} className="capitalize">{status?.replace('_', ' ')}</Badge>;
  };

  const getProcessingStatus = (meeting) => {
    const statuses = [];
    if (meeting.recording_status === 'completed') statuses.push({ icon: Mic, color: 'text-emerald-500', label: 'Recorded' });
    if (meeting.transcription_status === 'completed') statuses.push({ icon: FileText, color: 'text-blue-500', label: 'Transcribed' });
    if (meeting.mom_status === 'approved') statuses.push({ icon: CheckCircle, color: 'text-purple-500', label: 'MoM Ready' });
    return statuses;
  };

  return (
    <div className="p-6 space-y-6" data-testid="meetings-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/meeting-intelligence')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <Calendar className="h-7 w-7 text-blue-600" />
              Meetings
            </h1>
            <p className="text-slate-500 mt-1">{pagination.total} meetings total</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/meeting-intelligence/meetings/new')}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Meeting
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search meetings..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filters.meeting_type}
              onValueChange={(v) => setFilters(prev => ({ ...prev, meeting_type: v }))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="physical">Physical</SelectItem>
                <SelectItem value="teams">Teams</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={() => fetchMeetings(1)}>
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Meetings List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No meetings found</p>
              <p className="text-sm mt-1">Create your first meeting to get started</p>
              <Button 
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/meeting-intelligence/meetings/new')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Meeting
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {meetings.map((meeting) => (
                <div 
                  key={meeting.id}
                  className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/meeting-intelligence/meetings/${meeting.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        meeting.meeting_type === 'teams' ? 'bg-purple-100 dark:bg-purple-900/30' :
                        meeting.meeting_type === 'hybrid' ? 'bg-amber-100 dark:bg-amber-900/30' :
                        'bg-blue-100 dark:bg-blue-900/30'
                      }`}>
                        {getMeetingTypeIcon(meeting.meeting_type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                            {meeting.title}
                          </h3>
                          {getStatusBadge(meeting.status)}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {meeting.scheduled_start ? 
                              new Date(meeting.scheduled_start).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'Not scheduled'
                            }
                          </span>
                          {meeting.location && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {meeting.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {meeting.attendees?.length || 0} attendees
                          </span>
                        </div>
                        
                        {/* Processing Status Indicators */}
                        {getProcessingStatus(meeting).length > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            {getProcessingStatus(meeting).map((status, idx) => (
                              <span key={idx} className={`flex items-center gap-1 text-xs ${status.color}`}>
                                <status.icon className="h-3 w-3" />
                                {status.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/meeting-intelligence/meetings/${meeting.id}`);
                          }}>
                            <Eye className="h-4 w-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          {meeting.status === 'scheduled' && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/meeting-intelligence/meetings/${meeting.id}/record`);
                            }}>
                              <PlayCircle className="h-4 w-4 mr-2" /> Start Meeting
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/meeting-intelligence/meetings/${meeting.id}/edit`);
                          }}>
                            <Edit className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">
            Page {pagination.page} of {pagination.pages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => fetchMeetings(pagination.page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => fetchMeetings(pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingsList;
